import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * useDetectPageInteraction
 * Detects if the user has interacted with the page (typed, changed inputs, etc.).
 * Automatically resets when the route/pathname changes.
 */
export const useDetectPageInteraction = (
  containerRef?: React.RefObject<HTMLElement>
) => {
  const [dirty, setDirty] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const target = containerRef?.current || document;

    const markDirty = (e: Event) => {
      const el = e.target as HTMLElement;
      // Optional: ignore elements with data-ignore-exit
      if (el && el.closest("[data-ignore-exit]")) return;
      setDirty(true);
    };

    target.addEventListener("input", markDirty, { capture: true });
    target.addEventListener("change", markDirty, { capture: true });
    target.addEventListener("keydown", markDirty, { capture: true });

    return () => {
      target.removeEventListener("input", markDirty, { capture: true });
      target.removeEventListener("change", markDirty, { capture: true });
      target.removeEventListener("keydown", markDirty, { capture: true });
    };
  }, [containerRef]);

  // Reset dirty state whenever the route changes
  useEffect(() => {
    setDirty(false);
  }, [location.pathname]);

  return [dirty, setDirty] as const;
};
